/**
 * AdventureLogIndexPage — /adventure-logs blog index.
 * Chronological list of published posts from the build-time manifest.
 */
import { Helmet } from 'react-helmet-async';
import { Link } from '@tanstack/react-router';
import SchemaJsonLd from '../components/primitives/SchemaJsonLd.jsx';
import SectionHead from '../components/ui/SectionHead.jsx';
import Reveal from '../components/ui/Reveal.jsx';
import { posts } from '../content/adventure-logs-manifest.js';
import { adventureLogsIndex } from '../content/adventureLogs.js';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr || '';
  return d.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const blogIndexSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Blog',
      '@id': 'https://artificersai.com/adventure-logs#blog',
      name: 'Adventure Logs',
      description: adventureLogsIndex.tagline,
      url: 'https://artificersai.com/adventure-logs',
      inLanguage: 'en-AU',
      publisher: {
        '@type': 'Organization',
        name: "The Artificer's AI",
        url: 'https://artificersai.com',
      },
    },
    {
      '@type': 'ItemList',
      '@id': 'https://artificersai.com/adventure-logs#postList',
      itemListOrder: 'https://schema.org/ItemListOrderDescending',
      numberOfItems: posts.length,
      itemListElement: posts.map((post, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: post.canonical || `https://artificersai.com/adventure-logs/${post.slug}`,
        name: post.title,
      })),
    },
  ],
};

export default function AdventureLogIndexPage() {
  return (
    <>
      <Helmet>
        <title>Adventure Logs — The Artificer's AI</title>
        <meta
          name="description"
          content="Field notes from inside client builds. Short-to-medium essays on the systems we ship, the regulatory shifts that shape them, and the operator decisions behind the code."
        />
        <link rel="canonical" href="https://artificersai.com/adventure-logs" />
        <meta property="og:title" content="Adventure Logs — The Artificer's AI" />
        <meta
          property="og:description"
          content="Field notes from inside client builds. Short-to-medium essays on the systems we ship, the regulatory shifts that shape them, and the operator decisions behind the code."
        />
        <meta property="og:url" content="https://artificersai.com/adventure-logs" />
      </Helmet>

      <SchemaJsonLd data={blogIndexSchema} />

      <section className="section-pad bg-surface text-ink-50">
        <div className="container-page max-w-prose">
          <SectionHead
            eyebrow={adventureLogsIndex.eyebrow}
            heading={adventureLogsIndex.heading}
          />
          <Reveal as="p" delay={0} className="text-body-lg text-ink-200 mt-4 mb-12">
            {adventureLogsIndex.tagline}
          </Reveal>

          {posts.length === 0 ? (
            <p className="text-body text-ink-300">{adventureLogsIndex.emptyState}</p>
          ) : (
            <div className="flex flex-col">
              {posts.map((post) => (
                <Reveal
                  as="article"
                  key={post.slug}
                  delay={0}
                  className="border-t border-white/[.08] py-8 first:border-t-0 first:pt-0"
                >
                  <span className="font-mono text-mono-xs text-ink-300 uppercase tracking-wider">
                    {formatDate(post.date)}
                  </span>
                  <h2 className="text-h3 font-display font-semibold text-ink-50 mt-2 mb-3">
                    <Link
                      to="/adventure-logs/$slug"
                      params={{ slug: post.slug }}
                      className="hover:text-brand-blue-bright transition-colors"
                    >
                      {post.title}
                    </Link>
                  </h2>
                  <p className="text-body text-ink-200 mb-2">
                    {post.excerpt || post.description}
                  </p>
                  <span className="text-body-sm text-ink-300">By {post.author}</span>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
